import { DecimalPipe, JsonPipe } from '@angular/common';
import { Component, computed, effect, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FieldTree, FormField, SchemaPathTree } from '@angular/forms/signals';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { of } from 'rxjs';
import { ACB_IEC60947Dto, DeviceDto, Fuse_BS3036Dto, Fuse_BS88Dto, Fuse_IEC60269Dto, GeneralDeviceDto, MCB_IEC60898Dto, MCCB_IEC60947Dto, MceApiService, RCBO_IEC61009Dto, StringSelectDto } from '../../../core/mceapi.client.generated';
import { apiOpt, appOpt, getNoneOptionDto, getSomeOptionDto } from '../../../core/mceapi.client.helpers';
import { CalculationBoxComponent } from '../../../shared/components/calculation-box/calculation-box.component';
import { Option, bind, match, none, some } from '../../../shared/functions/functional';
import { Load, createLoadDto } from '../load/load.model';
import { CableSettings } from '../settings/cable-settings';
import { SystemVoltage, createSystemVoltageDto } from '../system-voltage/system-voltage.model';
import { ACBIEC60947, AcbComponent, buildACBIEC60947Schema, createACBIEC60947, createACBIEC60947Dto } from './acb/acb.component';
import { FuseBS3036, FuseBs3036Component, buildFuseBS3036Schema, createFuseBS3036, createFuseBS3036Dto } from './fuse-bs3036/fuse-bs3036.component';
import { FuseBS88, FuseBs88Component, buildFuseBS88Schema, createFuseBS88, createFuseBS88Dto } from './fuse-bs88/fuse-bs88.component';
import { FuseIEC60269, FuseIec60269Component, buildFuseIEC60269Schema, createFuseIEC60269, createFuseIEC60269Dto } from './fuse-iec60269/fuse-iec60269.component';
import { GeneralDevice, GeneralDeviceComponent, buildGeneralSchema, createGeneralDevice, createGeneralDeviceDto } from './general-device/general-device.component';
import { MCBIEC60898, McbComponent, buildMCBIEC60898Schema, createMCBIEC60898, createMCBIEC60898Dto } from './mcb/mcb.component';
import { MCCBIEC60947, MccbComponent, buildMCCBIEC60947Schema, createMCCBIEC60947, createMCCBIEC60947Dto } from './mccb/mccb.component';
import { RCBOIEC61009, RcboComponent, buildRCBOIEC61009Schema, createRCBOIEC61009, createRCBOIEC61009Dto } from './rcbo/rcbo.component';
import { CableCalculationsStore } from '../cable-calculations.store';

export interface Protection {
  deviceKind: string, // empty string for no device, otherwise the device type
  generalDevice: GeneralDevice,
  acbIEC60947: ACBIEC60947,
  mccbIEC60947: MCCBIEC60947,
  mcbIEC60898: MCBIEC60898,
  fuseIEC60269: FuseIEC60269,
  fuseBS3036: FuseBS3036,
  fuseBS88: FuseBS88;
  rcboIEC61009: RCBOIEC61009
}


export function buildProtectionSchema(path: SchemaPathTree<Protection>) {
  buildGeneralSchema(path.generalDevice);
  buildACBIEC60947Schema(path.acbIEC60947);
  buildMCCBIEC60947Schema(path.mccbIEC60947);
  buildMCBIEC60898Schema(path.mcbIEC60898);
  buildFuseIEC60269Schema(path.fuseIEC60269);
  buildFuseBS3036Schema(path.fuseBS3036);
  buildFuseBS88Schema(path.fuseBS88);
  buildRCBOIEC61009Schema(path.rcboIEC61009);
}


const getDeviceKind = (deviceOpt: Option<DeviceDto>) => match(
  () => '',
  (x: DeviceDto) => {
    if (x.deviceKind === undefined) {
      throw new Error('Protection DTO is missing its device kind.');
    }

    return x.deviceKind;
  })(deviceOpt);


export function createProtection(dto: Option<DeviceDto>) {
  return signal<Protection>({
    deviceKind: getDeviceKind(dto),
    generalDevice: createGeneralDevice(bind((x: DeviceDto) => appOpt(x.generalDevice ?? {}))(dto))(),
    acbIEC60947: createACBIEC60947(bind((x: DeviceDto) => appOpt(x.acB_IEC60947 ?? {}))(dto))(),
    mccbIEC60947: createMCCBIEC60947(bind((x: DeviceDto) => appOpt(x.mccB_IEC60947 ?? {}))(dto))(),
    mcbIEC60898: createMCBIEC60898(bind((x: DeviceDto) => appOpt(x.mcB_IEC60898 ?? {}))(dto))(),
    fuseIEC60269: createFuseIEC60269(bind((x: DeviceDto) => appOpt(x.fuse_IEC60269 ?? {}))(dto))(),
    fuseBS3036: createFuseBS3036(bind((x: DeviceDto) => appOpt(x.fuse_BS3036 ?? {}))(dto))(),
    fuseBS88: createFuseBS88(bind((x: DeviceDto) => appOpt(x.fuse_BS88 ?? {}))(dto))(),
    rcboIEC61009: createRCBOIEC61009(bind((x: DeviceDto) => appOpt(x.rcbO_IEC61009 ?? {}))(dto))()
  });
}

export function createProtectionDto(protection: FieldTree<Protection>) {

  let base = {
    deviceKind: protection.deviceKind().value(),
    generalDevice: getNoneOptionDto<GeneralDeviceDto>(),
    acB_IEC60947: getNoneOptionDto<ACB_IEC60947Dto>(),
    mccB_IEC60947: getNoneOptionDto<MCCB_IEC60947Dto>(),
    mcB_IEC60898: getNoneOptionDto<MCB_IEC60898Dto>(),
    fuse_IEC60269: getNoneOptionDto<FuseIEC60269>(),
    fuse_BS3036: getNoneOptionDto<FuseBS3036>(),
    fuse_BS88: getNoneOptionDto<FuseBS88>(),
    rcbO_IEC61009: getNoneOptionDto<RCBOIEC61009>()
  } as DeviceDto;

  switch (protection.deviceKind().value()) {

    case 'GeneralProtectiveDevice': return signal<Option<DeviceDto>>(
      some({
        ...base, generalDevice: getSomeOptionDto<GeneralDeviceDto>(createGeneralDeviceDto(protection.generalDevice)())
      }));

    case 'ACB_IEC60947': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: ACB_IEC60947Dto) => some({ ...base, acB_IEC60947: getSomeOptionDto<ACB_IEC60947Dto>(x) }))
        (createACBIEC60947Dto(protection.acbIEC60947)()));

    case 'MCCB_IEC60947': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: MCCB_IEC60947Dto) => some({ ...base, mccB_IEC60947: getSomeOptionDto<MCCB_IEC60947Dto>(x) }))
        (createMCCBIEC60947Dto(protection.mccbIEC60947)()));

    case 'MCB_IEC60898': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: MCB_IEC60898Dto) => some({ ...base, mcB_IEC60898: getSomeOptionDto<MCB_IEC60898Dto>(x) }))
        (createMCBIEC60898Dto(protection.mcbIEC60898)()));
 
    case 'Fuse_IEC60269': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: Fuse_IEC60269Dto) => some({ ...base, fuse_IEC60269: getSomeOptionDto<Fuse_IEC60269Dto>(x) }))
        (createFuseIEC60269Dto(protection.fuseIEC60269)()));

    case 'Fuse_BS3036': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: Fuse_BS3036Dto) => some({ ...base, fuse_BS3036: getSomeOptionDto<Fuse_BS3036Dto>(x) }))
        (createFuseBS3036Dto(protection.fuseBS3036)()));

    case 'Fuse_BS88': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: Fuse_BS88Dto) => some({...base, fuse_BS88: getSomeOptionDto<Fuse_BS88Dto>(x) }))
        (createFuseBS88Dto(protection.fuseBS88)())); 

    case 'RCBO_IEC61009': return signal<Option<DeviceDto>>(
      match(
        () => none,
        (x: RCBO_IEC61009Dto) => some({ ...base, rcbO_IEC61009: getSomeOptionDto<RCBO_IEC61009Dto>(x) }))
        (createRCBOIEC61009Dto(protection.rcboIEC61009)()));

  }

  return signal<Option<DeviceDto>>(
    (protection.deviceKind().value() === '') ? none : some(base));      

}

@Component({
  selector: 'cable-edit-protection',
  imports: [FormField, MatSelectModule, MatButtonToggleModule, MatCheckboxModule,
    MatTooltipModule, CalculationBoxComponent,

    DecimalPipe,

    AcbComponent, 
    GeneralDeviceComponent, McbComponent, MccbComponent, FuseBs88Component, FuseBs3036Component, FuseIec60269Component, RcboComponent, MatSelectModule],
  templateUrl: './cable-protection.component.html'
})

export class CableProtectionComponent {

  readonly form = input.required<FieldTree<Protection>>();
  readonly settings = input.required<FieldTree<CableSettings>>();
  readonly systemVoltage = input.required<FieldTree<SystemVoltage>>();
  readonly load = input.required<FieldTree<Load>>();


  private mceApiService = inject(MceApiService);
  private store = inject(CableCalculationsStore);

  deviceKindsRx = rxResource({
    params: () => ({ voltage: createSystemVoltageDto(this.systemVoltage()) }),
    stream: ({ params }) =>
      this.systemVoltage()().valid()
        ? this.mceApiService.v1ProtectionListVoltage(params.voltage())
        : of([])
  });
  deviceKinds = computed<StringSelectDto[]>(() => [
    { label: 'No Protective Device', value: '' },
    ...(this.deviceKindsRx.value() ?? []) ]);


  designCurrentRx = rxResource({
    params: () => ({
      load: createLoadDto(this.load())(),
      device: createProtectionDto(this.form())(),
      sdc: this.settings().simpleDesignCurrent().value(),
      valid: this.load()().valid() && this.form()().valid(),
    }),
    stream: ({ params }) =>
      params.valid
        ? this.mceApiService.v1CalculationDesignCurrent({
            load: params.load,
            device: apiOpt(params.device),
            simpleDesignCurrent: params.sdc
        })
        : of(undefined)
  });
  designCurrent = computed(() => this.designCurrentRx.value());

  designCurrentStatus = computed(() =>
    this.designCurrentRx.value()?.status
    ?? { case: 'Info', message:'Please ensure load and protection are valid to calculate design current.' }
  );

  _ = effect(() => {
    this.store.setDesignCurrent(this.designCurrentRx.value());
  });


}
